import { useRef, useState } from "react";
import { FieldErrors, FieldPath, FormProvider, useForm } from "react-hook-form";
import { BsCheck } from "react-icons/bs";
import { toast } from "react-toastify";
import {
	useDraftMerchantRequest,
	useFinalizeMerchantRequestDraft,
	useOnboardMerchants,
	useSubmitUpdateOnboardedMerchantRequest,
	useUpdateDraftedMerchantRequest,
	useUpdateSubmittedMerchantRequest,
} from "../../../../../services/payment-setup.service";
import BusinessInfoStep from "./steps/BusinessInfoStep";
import ContactStep from "./steps/ContactStep";
import DirectorsStep from "./steps/DirectorsStep";
import DocumentsStep from "./steps/DocumentsStep";
import ProductsStep from "./steps/ProductsStep";
import RequestSummary from "./RequestSummary";
import {
	PaymentSetupFormValues,
	formValuesToPayload,
	getSavedRecordIds,
	toRecord,
} from "./helpers";
import { PaymentSetupDto } from "../../../../../models/payment-setup.dto";

/**
 * - `create`: a new request, saved as a draft after each step and finalised on submit
 * - `correct`: fixing a request that has already been submitted
 * - `update`: changing the details of a merchant that is already live
 */
export type WizardMode = "create" | "correct" | "update";

type Step = {
	key: string;
	title: string;
	fields: FieldPath<PaymentSetupFormValues>[];
};

const ALL_STEPS: Step[] = [
	{
		key: "business",
		title: "Business",
		fields: [
			"companyName",
			"tradeName",
			"alias",
			"taxIdentificationNumber",
			"tier",
			"country",
			"companyCategories",
			"companyRegistrationNumber",
			"vatRegistrationNumber",
			"companyLogo",
		],
	},
	{
		key: "contact",
		title: "Contact & address",
		fields: ["surname", "otherNames", "email", "phoneNumber", "officeAddress", "contacts"],
	},
	{ key: "directors", title: "Directors", fields: ["companyDirectors"] },
	{ key: "documents", title: "Documents", fields: ["documents"] },
	{ key: "products", title: "Products", fields: ["products"] },
	{ key: "review", title: "Review", fields: [] },
];

interface PaymentSetupWizardProps {
	companyId: number;
	mode: WizardMode;
	initialValues: PaymentSetupFormValues;
	/** Draft id in `create` mode, or the submitted request id otherwise */
	setupId?: number;
	onCancel?: () => void;
	cancelLabel?: string;
	onSubmitted: () => void;
}

const PaymentSetupWizard = ({
	companyId,
	mode,
	initialValues,
	setupId,
	onCancel,
	cancelLabel = "Cancel",
	onSubmitted,
}: PaymentSetupWizardProps) => {
	// Products on a live merchant are added through "Add products" instead
	const steps =
		mode === "update" ? ALL_STEPS.filter((step) => step.key !== "products") : ALL_STEPS;

	const methods = useForm<PaymentSetupFormValues>({
		defaultValues: initialValues,
		mode: "onTouched",
	});
	const { handleSubmit, trigger, getValues, setValue } = methods;

	const [currentStep, setCurrentStep] = useState(0);
	const [furthestStep, setFurthestStep] = useState(0);
	const [draftId, setDraftId] = useState<number | undefined>(
		mode === "create" ? setupId : undefined,
	);
	const [lastSavedAt, setLastSavedAt] = useState<Date>();
	const containerRef = useRef<HTMLDivElement>(null);

	const { mutateAsync: createDraft, isLoading: creatingDraft } = useDraftMerchantRequest();
	const { mutateAsync: updateDraft, isLoading: updatingDraft } =
		useUpdateDraftedMerchantRequest();
	const { mutate: onboardMerchant, isLoading: onboarding } = useOnboardMerchants();
	const { mutate: finalizeDraft, isLoading: finalizing } = useFinalizeMerchantRequestDraft();
	const { mutate: updateSubmittedRequest, isLoading: correcting } =
		useUpdateSubmittedMerchantRequest();
	const { mutate: updateOnboardedMerchant, isLoading: updatingMerchant } =
		useSubmitUpdateOnboardedMerchantRequest();

	const savingDraft = creatingDraft || updatingDraft;
	const submitting = onboarding || finalizing || correcting || updatingMerchant;
	const isReviewStep = steps[currentStep].key === "review";

	const goToStep = (step: number) => {
		setCurrentStep(step);
		setFurthestStep((furthest) => Math.max(furthest, step));
		containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const saveDraft = async (showToast = false) => {
		const payload = formValuesToPayload(getValues(), { draft: true });
		try {
			const response = draftId
				? await updateDraft({ id: draftId, companyId, payload })
				: await createDraft({ companyId, payload });
			const saved = toRecord<PaymentSetupDto>(response);
			if (!draftId && saved?.id) setDraftId(saved.id);

			// Keep the ids of newly saved documents, directors, etc. so the next save
			// updates them rather than adding duplicates
			if (saved) {
				getSavedRecordIds(getValues(), saved).forEach(({ path, id }) =>
					setValue(path as any, id as any),
				);
			}
			setLastSavedAt(new Date());
			if (showToast) toast.success("Draft saved");
		} catch {
			// The mutation's error handler has already shown the reason
		}
	};

	const handleContinue = async () => {
		const valid = await trigger(steps[currentStep].fields);
		if (!valid) return;

		if (mode === "create") await saveDraft();
		goToStep(currentStep + 1);
	};

	const onValid = (values: PaymentSetupFormValues) => {
		const payload = formValuesToPayload(values);
		const onSuccess = () => {
			toast.success(
				mode === "create"
					? "Payment setup submitted for review"
					: "Your changes have been submitted",
			);
			onSubmitted();
		};

		if (mode === "correct" && setupId) {
			updateSubmittedRequest({ id: setupId, payload }, { onSuccess });
		} else if (mode === "update" && setupId) {
			updateOnboardedMerchant(
				{ id: setupId, payload: { ...payload, products: undefined } },
				{ onSuccess },
			);
		} else if (draftId) {
			finalizeDraft({ id: draftId, companyId, payload }, { onSuccess });
		} else {
			onboardMerchant({ companyId, payload }, { onSuccess });
		}
	};

	// Send the user back to the first step that still has a problem
	const onInvalid = (errors: FieldErrors<PaymentSetupFormValues>) => {
		const errorFields = Object.keys(errors);
		const stepWithError = steps.findIndex((step) =>
			step.fields.some((field) => errorFields.includes(field.split(".")[0])),
		);
		if (stepWithError >= 0) {
			toast.error("Some details need attention before you can submit");
			goToStep(stepWithError);
		}
	};

	const title =
		mode === "correct"
			? "Edit payment setup request"
			: mode === "update"
			? "Update merchant details"
			: "Set up payments";

	return (
		<FormProvider {...methods}>
			<div ref={containerRef}>
				<div className="flex justify-between items-start mb-6">
					<div>
						<h3 className="font-semibold text-lg">{title}</h3>
						<p className="text-xs text-gray-500 mt-1">
							{mode === "create"
								? lastSavedAt
									? `Draft saved at ${lastSavedAt.toLocaleTimeString(undefined, {
											timeStyle: "short",
									  })}`
									: "Your progress is saved as a draft after each step."
								: "Only the details you change will be updated."}
						</p>
					</div>
					<div className="flex items-center gap-3">
						{onCancel && (
							<button
								type="button"
								onClick={onCancel}
								className="h-10 px-4 text-sm text-gray-600 hover:text-gray-800"
							>
								{cancelLabel}
							</button>
						)}
						{mode === "create" && (
							<button
								type="button"
								onClick={() => saveDraft(true)}
								disabled={savingDraft}
								className="h-10 px-4 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
							>
								{savingDraft ? "Saving..." : "Save draft"}
							</button>
						)}
					</div>
				</div>

				{/* Stepper */}
				<ol className="flex items-center mb-8">
					{steps.map((step, index) => {
						const isCurrent = index === currentStep;
						const isDone = index < currentStep;
						const canNavigate = index <= furthestStep && !isCurrent;
						return (
							<li key={step.key} className="flex items-center flex-1 last:flex-none">
								<button
									type="button"
									disabled={!canNavigate}
									onClick={() => goToStep(index)}
									className="flex items-center gap-2 shrink-0 disabled:cursor-default"
								>
									<span
										className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${
											isCurrent
												? "bg-primary-mango text-white"
												: isDone
												? "bg-primary-blue text-white"
												: "bg-gray-100 text-gray-500"
										}`}
									>
										{isDone ? <BsCheck className="text-base" /> : index + 1}
									</span>
									<span
										className={`text-xs whitespace-nowrap ${
											isCurrent ? "font-semibold text-charcoal" : "text-gray-500"
										}`}
									>
										{step.title}
									</span>
								</button>
								{index < steps.length - 1 && (
									<span
										className={`flex-1 h-px mx-3 ${isDone ? "bg-primary-blue" : "bg-gray-200"}`}
									/>
								)}
							</li>
						);
					})}
				</ol>

				{/* All steps stay mounted so final validation covers every field */}
				{steps.map((step, index) => (
					<div key={step.key} className={index === currentStep ? "" : "hidden"}>
						{step.key === "business" && (
							<BusinessInfoStep readOnlyIdentity={mode === "update"} />
						)}
						{step.key === "contact" && <ContactStep />}
						{step.key === "directors" && <DirectorsStep />}
						{step.key === "documents" && <DocumentsStep />}
						{step.key === "products" && <ProductsStep />}
						{step.key === "review" && index === currentStep && (
							<div>
								<h3 className="font-semibold text-base text-charcoal">Review & submit</h3>
								<p className="text-xs text-gray-500 mt-1 mb-5">
									Check everything carefully. The provider usually responds with any
									corrections within a few working days.
								</p>
								<RequestSummary
									values={getValues()}
									onEditStep={(stepKeyIndex) => {
										const target = steps.findIndex(
											(item) => item.key === ALL_STEPS[stepKeyIndex].key,
										);
										if (target >= 0) goToStep(target);
									}}
								/>
							</div>
						)}
					</div>
				))}

				<div className="flex justify-between items-center mt-10 pt-6 border-t">
					<button
						type="button"
						onClick={() => goToStep(currentStep - 1)}
						disabled={currentStep === 0}
						className="h-12 px-6 border border-gray-300 rounded-lg text-sm text-gray-600 disabled:invisible"
					>
						Back
					</button>
					{isReviewStep ? (
						<button
							type="button"
							onClick={handleSubmit(onValid, onInvalid)}
							disabled={submitting}
							className="bg-primary-mango text-white h-12 px-8 rounded-lg text-sm disabled:opacity-50"
						>
							{submitting
								? "Submitting..."
								: mode === "create"
								? "Submit for review"
								: "Submit changes"}
						</button>
					) : (
						<button
							type="button"
							onClick={handleContinue}
							disabled={savingDraft}
							className="bg-primary-mango text-white h-12 px-8 rounded-lg text-sm disabled:opacity-50"
						>
							{savingDraft ? "Saving..." : "Continue"}
						</button>
					)}
				</div>
			</div>
		</FormProvider>
	);
};

export default PaymentSetupWizard;
